import { BASE_URL } from "@/lib/axios";

export type CacheTag =
  | "all"
  | "movies"
  | "series"
  | "home"
  | "home-blocks"
  | "home-slug"
  | `home-slug-${string}`
  | "pages"
  | "tags";

interface ServerFetchOptions {
  revalidate?: number | false;
  tags?: CacheTag[];
  headers?: Record<string, string>;
  params?: Record<string, string | number | boolean | null | undefined>;
}


const CACHE_THRESHOLD_MS = 10; // responses faster than this are almost certainly from cache

function cacheLabel(durationMs: number): string {
  if (durationMs < CACHE_THRESHOLD_MS) return "✅ CACHE HIT";
  if (durationMs < 100)              return "⚡ FAST";
  if (durationMs < 500)              return "🌐 NETWORK";
  return                                    "🐢 SLOW";
}

function log(
  status: number,
  method: string,
  pathname: string,
  durationMs: number,
  tags: string[],
  revalidate: number | false,
) {
  const label = cacheLabel(durationMs);
  const ttl   = revalidate === false ? "∞" : `${revalidate}s`;
  console.log(
    `[serverFetch] ${label}  ${method} ${status}  ${pathname}  ${durationMs.toFixed(1)}ms  ` +
    `ttl=${ttl}  tags=[${tags.join(",")}]`,
  );
}



export async function serverFetch<T>(
  path: string,
  options: ServerFetchOptions = {},
): Promise<T> {
  const {
    revalidate = 300,
    tags = [],
    headers = {},
    params,
  } = options;

  const url = new URL(`${BASE_URL}${path}`);

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, String(value));
      }
    }
  }

  const allTags: string[] = ["all", ...tags];

  const cacheOptions: RequestInit["next"] =
    revalidate === 0
      ? { revalidate: 0 }
      : { revalidate: revalidate === false ? false : revalidate, tags: allTags };

  const t0 = performance.now();

  const response = await fetch(url.toString(), {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...headers,
    },
    next: cacheOptions,
  });

  const durationMs = performance.now() - t0;

  log(response.status, "GET", url.pathname + url.search, durationMs, allTags, revalidate);

  if (!response.ok) {
    throw new Error(
      `serverFetch ${response.status}: ${response.statusText} — ${url.pathname}`,
    );
  }

  return response.json() as Promise<T>;
}
